import {
  EmployeesService,
  RequestItem,
  useCurrentRequestById,
  useOptionsApi,
  useProfileAPI,
  useResizeObserver,
  useSelectOptionsApi,
} from '@innowise-group/core';
import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTheme } from '@innowise-group/mui-kit';
import { DesktopView } from './components/desktop-view';
import { MobileView } from './components/mobile-view';
import { localizedNameObject } from '@innowise-group/utilities';
import { ManagerRolesArray } from '@constants';

interface RequestFormProps {
  isEdit?: boolean;
  onSubmit: (data: RequestItem) => void;
  handleClose: () => void;
  selectedCustomersOptions: string[];
  isNonBillableWarningToBeShown?: boolean;
}

const RequestForm: React.FC<RequestFormProps> = ({
  isEdit,
  isNonBillableWarningToBeShown,
  onSubmit,
  handleClose,
  selectedCustomersOptions,
}) => {
  const theme = useTheme();
  const isDesktopView = useResizeObserver(theme.breakpoints.values.sm);
  const { setValue, watch } = useFormContext<RequestItem>();
  const currentRequestEdition = useCurrentRequestById();
  const { locationOptions, countryOptions, officeOptions } = useOptionsApi();
  const { profile } = useProfileAPI();
  const selectedOffices = watch('offices');
  const countries = watch('countries');
  const cities = watch('cities');

  const isHiringManager = useMemo(() => {
    return profile?.role?.name && ManagerRolesArray.includes(profile?.role?.name);
  }, [profile]);

  const locationCountryOptions = useMemo(() => {
    return (countryOptions || []).filter((item) =>
      (locationOptions || []).some((curr) => curr.parent.toString() === item.value.toString()),
    );
  }, [locationOptions, countryOptions]);

  // The backend accepts a city of the selected countries only and an office of the selected cities only
  const locationCityOptions = useMemo(() => {
    return (locationOptions || []).filter((item) => countries?.some((curr) => curr.value === item.parent));
  }, [locationOptions, countries]);

  const offices = useMemo(() => {
    return officeOptions?.filter((item) => cities?.some((curr) => curr.value === item.parent));
  }, [officeOptions, cities]);

  const disabled = useMemo(() => !!currentRequestEdition?.candidatesFound && isEdit, [isEdit, currentRequestEdition]);

  const getEmployeeById = async (id: string) => {
    const { data } = await EmployeesService.getEmployeeById(Number(id));
    const { localizedFullName } = localizedNameObject(data);
    return {
      title: localizedFullName,
      value: data.id.toString(),
    };
  };

  const getInitialEmployeeOptions = async (partOfName: string) => {
    const { data } = await EmployeesService.searchEmployees({
      partOfName,
      page: 1,
      size: 10,
    });

    return data.content.map(({ employeeId, ...data }) => {
      return {
        title: localizedNameObject(data).localizedFullName,
        value: employeeId.toString(),
      };
    });
  };

  const { selectOptions: requestCustomerOptions, searchOptions: searchRequestCustomerOptions } = useSelectOptionsApi({
    getOptionsByName: getInitialEmployeeOptions,
    getOptionsById: getEmployeeById,
    selectedOptions: selectedCustomersOptions,
  });

  useEffect(() => {
    if (!countries?.length) {
      setValue('cities', []);
      return;
    }
    const filteredCities = cities.filter((item) => countries.find((curr) => curr.value === item.parent));
    setValue('cities', filteredCities);
  }, [countries]);

  useEffect(() => {
    if (!cities?.length) {
      setValue('offices', []);
      return;
    }
    const filteredOffices = selectedOffices.filter((item) => cities.find((curr) => curr.value === item.parent));
    setValue('offices', filteredOffices);
  }, [cities]);

  const Component = isDesktopView ? DesktopView : MobileView;

  return (
    <Component
      isHiringManager={isHiringManager}
      isEdit={isEdit}
      isNonBillableWarningToBeShown={isNonBillableWarningToBeShown}
      locationCountryOptions={locationCountryOptions}
      disabled={disabled}
      locationCityOptions={locationCityOptions}
      offices={offices}
      currentRequestEdition={currentRequestEdition}
      onSubmit={onSubmit}
      handleClose={handleClose}
      requestCustomerOptions={requestCustomerOptions}
      searchRequestCustomerOptions={searchRequestCustomerOptions}
    />
  );
};

export default RequestForm;
